/**
 * ClassificationBanner.tsx
 *
 * Persistent classification marking banner component for displaying security
 * classification levels at the top and bottom of the EMS application.
 *
 * Features:
 * - Displays classification marking (e.g., "SECRET//NOFORN - EMS PROD")
 * - Fixed position at top or bottom of viewport
 * - Color-coordinated to classification level
 * - Accessible with proper ARIA attributes
 * - Only renders if classification-banners module is enabled and assignment exists
 * - Responsive with proper text truncation on mobile
 *
 * Styling:
 * - Fixed height: 20px (reduced 30% from original 28px)
 * - Font size: 12px (reduced from 14px for proportional scaling)
 * - Bold text
 * - Centered text
 * - Full width with no wrapping (ellipsis overflow)
 * - High z-index to stay on top
 *
 * Usage:
 * ```tsx
 * <ClassificationBanner position="top" />
 * <ClassificationBanner position="bottom" />
 * ```
 */

import { memo } from 'react';
import { useClassificationBanner } from '@/base/hooks/useClassificationBanner';

// ============================================================================
// Types
// ============================================================================

export interface ClassificationBannerProps {
  /** Position of banner - top or bottom of viewport */
  position: 'top' | 'bottom';
}

// ============================================================================
// Constants
// ============================================================================

const BANNER_HEIGHT = '20px';
const BANNER_FONT_SIZE = '12px';
const BANNER_Z_INDEX = 9999;

// ============================================================================
// Component
// ============================================================================

function ClassificationBannerComponent({
  position,
}: ClassificationBannerProps) {
  const {
    enabled,
    showTopBanner,
    showBottomBanner,
    fullText,
    backgroundColor,
    textColor,
    screenReaderText,
    isLoading,
  } = useClassificationBanner();

  // Don't render if module is disabled or no assignment exists
  if (!enabled) {
    return null;
  }

  // Don't render while loading to prevent flash
  if (isLoading) {
    return null;
  }

  // Check if this specific banner position should be shown
  const shouldShow = position === 'top' ? showTopBanner : showBottomBanner;
  if (!shouldShow) {
    return null;
  }

  // Don't render if we don't have the required data
  if (!fullText || !backgroundColor || !textColor) {
    return null;
  }

  // Determine ARIA role based on position
  const ariaRole = position === 'top' ? 'banner' : 'contentinfo';

  // Determine position classes and styles
  const positionClass = position === 'top' ? 'top-0' : 'bottom-0';

  return (
    <div
      className={`
        fixed
        ${positionClass}
        left-0
        right-0
        flex
        items-center
        justify-center
        font-bold
        text-center
        whitespace-nowrap
        overflow-hidden
        px-4
      `}
      style={{
        height: BANNER_HEIGHT,
        fontSize: BANNER_FONT_SIZE,
        backgroundColor: backgroundColor,
        color: textColor,
        zIndex: BANNER_Z_INDEX,
        lineHeight: BANNER_HEIGHT,
      }}
      role={ariaRole}
      aria-label={screenReaderText || `Classification Banner: ${fullText}`}
      data-testid={`classification-banner-${position}`}
    >
      {/* Banner text with ellipsis overflow */}
      <span
        className="
          block
          overflow-hidden
          text-ellipsis
          whitespace-nowrap
          max-w-full
        "
      >
        {fullText}
      </span>
    </div>
  );
}

// ============================================================================
// Memoized Export
// ============================================================================

/**
 * Classification banner component with memoization for optimal performance.
 * Memoization prevents unnecessary re-renders when parent components update.
 */
export const ClassificationBanner = memo(ClassificationBannerComponent);

ClassificationBanner.displayName = 'ClassificationBanner';

export default ClassificationBanner;
